import * as React from 'react';
import { Helmet } from 'react-helmet';

import { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';
import { push as dataLayerPush } from 'helper/ga';
import i18n, { labelExists } from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import { template } from 'helper/string';

import LevelOverview from 'containers/molecules/LevelOverview';

import Coach from 'components/atoms/Coach';
import ProgressRadial from 'components/atoms/ProgressRadial';
import Bevel from 'components/helper/Bevel';
import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import FameLevel, { FameLevelLevels } from 'components/molecules/Fame/Level';
import LinkList from 'components/molecules/LinkList';
import Overlay from 'components/molecules/Overlay';
import { trackMySuccessAccess } from 'defs/ga/track/mySuccess';

type FameStageProps = {
	currentViewID: string;
	points: number | null;
	nextLevelThreshold: number | null;
	coach: CoachIsEnumType | null;
	level: string | null;
};

type FameStageState = {
	isOverlayOpen: boolean;
};

export default class FameStage extends React.Component<FameStageProps, FameStageState> {
	public state = {
		isOverlayOpen: false,
	};

	public handleShowAllLevelsClick = () => {
		this.setState({ isOverlayOpen: true });
	};

	public handleLevelOverlayClose = () => {
		this.setState({ isOverlayOpen: false });
	};

	public renderProgress() {
		const { points, nextLevelThreshold } = this.props;

		if (points === null || nextLevelThreshold === null) {
			return null;
		}

		return (
			<ProgressRadial
				reach={points}
				total={nextLevelThreshold}
				percent={Math.round((points * 100) / nextLevelThreshold)}
				text={i18n(`react.impulsCoach.components.fameStage.points`)}
				meterColor="0099A6"
				valueColor="48BFA2"
				modifiers={['multiline']}
			/>
		);
	}

	public renderLinkList() {
		const infoScoring = {
			label: `react.impulsCoach.components.fameStage.infoScoring.label`,
			href: `react.impulsCoach.components.fameStage.infoScoring.href`,
		};
		const links: any[] = [
			{
				title: i18n(`react.impulsCoach.components.fameStage.showAllLevels`),
				onClick: this.handleShowAllLevelsClick,
			},
		];
		if (
			labelExists(infoScoring.label) &&
			i18n(infoScoring.label).length > 0 &&
			labelExists(infoScoring.href) &&
			i18n(infoScoring.href).length > 0
		) {
			links.push({
				title: i18n(infoScoring.label),
				externalHref: i18n(infoScoring.href),
				openInNewWindow: true,
			});
		}
		return <LinkList links={links} />;
	}

	public renderCoach() {
		const { coach } = this.props;

		return (
			coach && (
				<div className="col-xs-offset-6 col-xs-6 col-md-offset-0 col-md-4">
					<div className="m-fame-stage__coach-box">
						<div className="m-fame-stage__coach-wrap">
							<Coach coach={coach} gesture="full-good" alt="" />
						</div>
					</div>
				</div>
			)
		);
	}

	get seoData(): ReturnType<typeof getSeoDataFromViewId> {
		const { currentViewID } = this.props;
		return getSeoDataFromViewId(currentViewID);
	}

	public googleAnalyticsTracking() {
		const { level, points } = this.props;

		if (level && points) {
			dataLayerPush(
				trackMySuccessAccess({
					coachProgAction: 'level',
					coachLanguage: appConfig.env.language,
					coachLevelStatus: level,
					coachLevelPoints: points.toString(),
				})
			);
		}
	}
	public componentDidMount() {
		this.googleAnalyticsTracking();
	}

	public componentDidUpdate(prevProps: FameStageProps): void {
		if (prevProps.points !== this.props.points) {
			this.googleAnalyticsTracking();
		}
	}

	public render() {
		const { level } = this.props;
		const seoData = this.seoData;
		const seoLevel = `${FameLevelLevels[level || 'L00']}` || '0';
		const compiledSeoTitle = template(seoData.title, {
			level: seoLevel,
		});
		const compiledOgTitle = template(seoData.og ? seoData.og.title : '', {
			level: seoLevel,
		});

		return (
			<div>
				<Helmet>
					<title>{`${compiledSeoTitle} ${getBaseTitle()}`}</title>
					<meta property="og:title" content={compiledOgTitle} />
				</Helmet>
				<Overlay
					id="m-overlay-fame"
					contentStyle="grid"
					isOpen={this.state.isOverlayOpen}
					closable
					closeButton
					animation="move"
					onClose={this.handleLevelOverlayClose}
				>
					<LevelOverview />
				</Overlay>
				<div className="m-fame-stage">
					<div className="container container--full-width-ms">
						<div className="row">
							<div className="col-xs-6 col-md-4">
								<div className="m-fame-stage__level">
									<FameLevel level={level} doubleDigit />
								</div>
							</div>
							<div className="col-xs-6 col-md-4">
								<div className="m-fame-stage__progress">{this.renderProgress()}</div>
								<div className="m-fame-stage__links">{this.renderLinkList()}</div>
							</div>
							{this.renderCoach()}
						</div>
					</div>
					<Bevel modifier="bottom-left" color="ffffff" />
				</div>
			</div>
		);
	}
}
